import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Box,
  Avatar,
  TextField,
  Tooltip
} from '@mui/material';
import logoImage from '../../images/logo.webp';
import {
  AccountCircle,
  AccountBalanceWallet,
  Notifications,
  Work,
  Dashboard,
  ExitToApp,
  Menu as MenuIcon,
  Event,
  Language,
  Check,
  RateReview,
  WarningAmber,
  GetApp
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { usePWAInstall } from '../../contexts/PWAInstallContext';
import AutoFitText from '../Common/AutoFitText';
import TranslatedText from '../Common/TranslatedText';

const NAV_SINGLELINE_SX = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'clip', // no dots
  whiteSpace: 'nowrap',
};

// Keep desktop nav buttons visually consistent (prevents AutoFitText from shrinking longer labels)
const NAV_DESKTOP_BUTTON_MAX_WIDTH = 170;

const LANGUAGE_NAME_OVERRIDES_EN = {
  // Legacy/alias codes sometimes used by Google Translate
  iw: 'Hebrew',
  jw: 'Javanese',
  in: 'Indonesian',
  'zh-CN': 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
};

const LANGUAGE_CODE_ALIASES = {
  iw: 'he',
  jw: 'jv',
  in: 'id',
};

const normalizeLangCode = (rawCode) => {
  if (!rawCode) return '';
  const code = String(rawCode).replace(/_/g, '-');
  return LANGUAGE_CODE_ALIASES[code] || code;
};

const getEnglishLanguageName = (rawCode) => {
  if (!rawCode) return '';
  const code = normalizeLangCode(rawCode);
  if (LANGUAGE_NAME_OVERRIDES_EN[code]) return LANGUAGE_NAME_OVERRIDES_EN[code];
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined') {
      const dn = new Intl.DisplayNames(['en'], { type: 'language' });
      const name = dn.of(code);
      if (name) return name;
    }
  } catch (_) {}
  return '';
};

const getNativeLanguageName = (rawCode) => {
  const code = normalizeLangCode(rawCode);
  if (!code) return '';
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined') {
      // Ask for the language name in its own language/script (e.g. kn -> ಕನ್ನಡ)
      const dn = new Intl.DisplayNames([code], { type: 'language' });
      const name = dn.of(code);
      if (name) return name;
    }
  } catch (_) {}
  return '';
};

// Stable host for Google Translate dropdown managed globally by index.html
const TranslateDropdownSlot = React.memo(() => (
  <Box
    id="google_translate_element_navbar"
    sx={{
      position: 'absolute',
      left: '-9999px',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      opacity: 0,
      '& .goog-te-combo': {
        color: '#DDD0C8',
        backgroundColor: '#323232',
        borderColor: '#DDD0C8',
        padding: '6px',
        borderRadius: '4px'
      },
      '& .goog-te-gadget': {
        color: '#DDD0C8'
      },
      '& .goog-te-gadget-simple': {
        backgroundColor: 'transparent',
        border: 'none'
      }
    }}
  />
));

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isAuthenticated, hasToken, logout, hasActiveServices } = useAuth();
  const { unreadCount } = useSocket();
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const translateFailed = typeof window !== 'undefined' && window.__ulcaapp_translate_failed;

  const profileMenuButtonRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  const workerNavLabel = hasActiveServices ? 'My Services' : 'Become a Worker';

  const broadcastGoogleTranslateLang = (value) => {
    try {
      localStorage.setItem('ulcaapp_gt_lang', value || '');
    } catch (_) {}
    try {
      window.dispatchEvent(
        new CustomEvent('ulcaapp:gtLangChange', { detail: { lang: value || '' } })
      );
    } catch (_) {}
  };
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileAnchor, setMobileAnchor] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const [langQuery, setLangQuery] = useState('');
  const [langOptions, setLangOptions] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  // Translate dropdown is initialized globally from index.html

  const ensureTranslateLoaded = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.__ulcaapp_loadGoogleTranslate === 'function') {
        window.__ulcaapp_loadGoogleTranslate();
      }
    } catch (_) {}
  };

  const selectedLangLabel = useMemo(() => {
    // Default state: show placeholder text until a language is chosen
    if (!selectedLang) return 'Select Language';
    const code = normalizeLangCode(selectedLang);
    if (!code || code === 'en') return 'English';
    return (
      getNativeLanguageName(code) ||
      getEnglishLanguageName(code) ||
      'Select Language'
    );
  }, [selectedLang]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    handleMobileMenuClose();
    navigate('/');
  };

  const getLanguageOptions = () => {
    const select = document.querySelector('#google_translate_element_navbar select.goog-te-combo');
    if (!select) {
      return { options: [], selectedValue: '' };
    }
    const englishLabels =
      (typeof window !== 'undefined' && window.__ulcaapp_translate_labels_en)
        ? window.__ulcaapp_translate_labels_en
        : null;
    const options = Array.from(select.options)
      .map((option) => ({
        value: option.value,
        // Keep language names in English even after the page is translated
        label:
          (englishLabels && englishLabels[option.value]) ||
          getEnglishLanguageName(option.value) ||
          option.textContent?.trim() ||
          ''
      }))
      .filter((option) => option.value && !/select language/i.test(option.label));
    return { options, selectedValue: select.value };
  };

  const handleOpenLanguageMenu = (event) => {
    // Lazy-load Google Translate only when user requests the language menu.
    ensureTranslateLoaded();
    const { options, selectedValue } = getLanguageOptions();
    setLangOptions(options);
    setSelectedLang(selectedValue);
    setLangQuery('');
    setLangAnchor(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setLangAnchor(null);
  };

  const handleSelectLanguage = (value) => {
    const select = document.querySelector('#google_translate_element_navbar select.goog-te-combo');
    if (select) {
      select.value = value;
      // Create and dispatch change event
      const changeEvent = document.createEvent('HTMLEvents');
      changeEvent.initEvent('change', true, false);
      select.dispatchEvent(changeEvent);
    }
    setSelectedLang(value);
    broadcastGoogleTranslateLang(value);
    handleCloseLanguageMenu();
  };

  const isActive = (path) => location.pathname === path;

  // Keep our React state in sync with the underlying Google Translate <select>
  useEffect(() => {
    let currentSelect = null;
    let hostEl = null;
    const onChange = () => {
      if (!currentSelect) return;
      const next = currentSelect.value || '';
      setSelectedLang(next);
      broadcastGoogleTranslateLang(next);
    };

    const attach = () => {
      const select = document.querySelector('#google_translate_element_navbar select.goog-te-combo');
      if (!select) return false;
      if (currentSelect === select) return true;
      if (currentSelect) {
        try {
          currentSelect.removeEventListener('change', onChange);
        } catch (_) {}
      }
      currentSelect = select;
      currentSelect.addEventListener('change', onChange);
      onChange(); // initial sync
      return true;
    };

    // Restore persisted selection (and trigger translate load if needed).
    try {
      const saved = localStorage.getItem('ulcaapp_gt_lang') || '';
      if (saved) {
        setSelectedLang(saved);
        if (saved !== 'en') ensureTranslateLoaded();
      }
    } catch (_) {}

    // Try immediately, then briefly while widget boots.
    attach();
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (attach() || tries >= 12) clearInterval(timer);
    }, 200);

    // Observe ONLY the widget host, not the entire document (full-document observers are expensive).
    try {
      hostEl = document.getElementById('google_translate_element_navbar');
      if (hostEl) {
        const mo = new MutationObserver(() => {
          // When Google injects/replaces the select/options, re-attach.
          if (attach()) {
            try { mo.disconnect(); } catch (_) {}
          }
        });
        mo.observe(hostEl, { childList: true, subtree: true });
        // Cleanup hook needs access
        hostEl.__ulcaapp_gt_mo = mo;
      }
    } catch (_) {}

    return () => {
      clearInterval(timer);
      try {
        if (hostEl && hostEl.__ulcaapp_gt_mo) hostEl.__ulcaapp_gt_mo.disconnect();
      } catch (_) {}
      try {
        if (currentSelect) currentSelect.removeEventListener('change', onChange);
      } catch (_) {}
    };
  }, []);

  // Google Translate can populate/replace the <select> options asynchronously.
  // While the language menu is open, keep refreshing options briefly so "missing" languages appear.
  useEffect(() => {
    if (!langAnchor) return;

    let cancelled = false;
    const refresh = () => {
      if (cancelled) return;
      const { options, selectedValue } = getLanguageOptions();
      if (options.length) setLangOptions(options);
      if (selectedValue) setSelectedLang(selectedValue);
    };

    refresh();
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      refresh();
      if (tries >= 12) clearInterval(timer);
    }, 300);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [langAnchor]);

  return (
    <AppBar 
      position="fixed"
      sx={{
        bgcolor: '#323232',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        zIndex: 1300,
        // Fill the iPhone notch / safe area so the bar's colour (not the page
        // background) shows behind the status bar in standalone/landscape.
        pt: 'env(safe-area-inset-top, 0px)',
        pl: 'env(safe-area-inset-left, 0px)',
        pr: 'env(safe-area-inset-right, 0px)',
        // Promote the fixed bar to its own compositing layer. This prevents the
        // iOS Safari repaint bug where a strip of page background flashes at the
        // top of the header while scrolling / when the address bar animates.
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center', minHeight: '48px !important', py: 0 }}>
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mr: 2,
            '&:hover': {
              opacity: 0.9
            }
          }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={logoImage}
              alt="UlcaApp Logo"
              sx={{
                height: { xs: 50, sm: 60, md: 70 },
                width: 'auto',
                maxHeight: 70,
                objectFit: 'contain',
                display: 'block'
              }}
            />
            <Box
              component="span"
              sx={{
                ml: 1,
                flexShrink: 0,
                px: 0.8,
                py: 0.25,
                borderRadius: 999,
                fontSize: { xs: 9, sm: 10, md: 11 },
                fontWeight: 800,
                letterSpacing: '0.06em',
                lineHeight: 1,
                backgroundColor: '#DDD0C8',
                color: '#323232'
              }}
            >
              BETA
            </Box>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right-side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Desktop Language selector - hidden on mobile */}
          <Tooltip
            title={translateFailed ? 'Language selector unavailable (blocked script). Check network/ad-blockers.' : ''}
            disableHoverListener={!translateFailed}
            disableFocusListener={!translateFailed}
            disableTouchListener={!translateFailed}
            arrow
          >
            <Button
              component="div"
              startIcon={<Language />}
              endIcon={translateFailed ? <WarningAmber fontSize="small" /> : undefined}
              disableRipple
              disableElevation
              onClick={handleOpenLanguageMenu}
              className="notranslate"
              translate="no"
              sx={{ 
                color: '#DDD0C8',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                cursor: 'pointer',
                mr: 1,
                display: { xs: 'none', md: 'flex' },
                minWidth: 0,
                maxWidth: 200,
              }}
            >
              <Box component="span" className="notranslate" translate="no" sx={NAV_SINGLELINE_SX}>
                <AutoFitText className="notranslate" translate="no" minFontSize={11}>
                  {selectedLangLabel}
                </AutoFitText>
              </Box>
            </Button>
          </Tooltip>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={handleCloseLanguageMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            className="notranslate"
            translate="no"
            PaperProps={{ 
              sx: { minWidth: 260, mt: 1 },
              onKeyDown: (e) => {
                // Prevent Menu from handling keyboard events when search input is focused
                const target = e.target;
                if (target && target.type === 'text') {
                  e.stopPropagation();
                }
              }
            }}
            MenuListProps={{
              className: 'notranslate',
              translate: 'no',
              onKeyDown: (e) => {
                // Prevent Menu keyboard navigation when search input is focused
                const target = e.target;
                if (target && target.type === 'text') {
                  e.stopPropagation();
                }
              }
            }}
          >
            <Box className="notranslate" translate="no" sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search language"
                value={langQuery}
                onChange={(event) => setLangQuery(event.target.value)}
                inputProps={{ 'aria-label': 'Search language', className: 'notranslate', translate: 'no' }}
                onKeyDown={(e) => {
                  // Stop propagation to prevent Menu from handling these events
                  e.stopPropagation();
                }}
                autoFocus
              />
            </Box>
            {langOptions
              .filter((option) =>
                option.label.toLowerCase().includes(langQuery.trim().toLowerCase())
              )
              .map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === selectedLang}
                  onClick={() => handleSelectLanguage(option.value)}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                >
                  <Box
                    component="span"
                    className="notranslate"
                    translate="no"
                    sx={{
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflowX: 'auto', // no wrap, no dots, no cutting (scroll if needed)
                      overflowY: 'hidden',
                      textOverflow: 'clip',
                    }}
                  >
                    {option.label}
                  </Box>
                  {option.value === selectedLang && <Check fontSize="small" />}
                </MenuItem>
              ))}
            {langOptions.length === 0 && (
              <MenuItem disabled>Language selector unavailable</MenuItem>
            )}
            {langOptions.length > 0 &&
              langOptions.filter((option) =>
                option.label.toLowerCase().includes(langQuery.trim().toLowerCase())
              ).length === 0 && (
                <MenuItem disabled>No matches</MenuItem>
              )}
          </Menu>
          <TranslateDropdownSlot />

          {/* Install App — desktop only; hidden automatically once installed */}
          {canInstall && (
            <Button
              startIcon={<GetApp />}
              onClick={() => promptInstall()}
              sx={{
                display: { xs: 'none', md: 'flex' },
                color: '#DDD0C8',
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                mr: 1,
                minWidth: 0,
                maxWidth: NAV_DESKTOP_BUTTON_MAX_WIDTH,
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}>Install App</AutoFitText>
              </Box>
            </Button>
          )}

          {(user || (hasToken && !loading) || isAuthenticated) ? (
            <>
            {/* Desktop actions */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {/* Account - after Choose Language */}
            <Button
              startIcon={<AccountBalanceWallet />}
              onClick={() => navigate('/profile#account')}
              sx={{ 
                color: '#DDD0C8',
                backgroundColor: location.pathname === '/profile' && location.hash === '#account' ? 'rgba(221, 208, 200, 0.1)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                minWidth: 0,
                maxWidth: NAV_DESKTOP_BUTTON_MAX_WIDTH,
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}>Account</AutoFitText>
              </Box>
            </Button>
            {/* Navigation Buttons */}
            <Button
              startIcon={<Dashboard />}
              onClick={() => navigate('/dashboard')}
              sx={{ 
                color: '#DDD0C8',
                backgroundColor: isActive('/dashboard') ? 'rgba(221, 208, 200, 0.1)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                minWidth: 0,
                maxWidth: NAV_DESKTOP_BUTTON_MAX_WIDTH,
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}>Dashboard</AutoFitText>
              </Box>
            </Button>

            <Button
              startIcon={<Work />}
              onClick={() => navigate('/worker/profile')}
              sx={{ 
                color: '#DDD0C8',
                backgroundColor: isActive('/worker/profile') ? 'rgba(221, 208, 200, 0.1)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                minWidth: 0,
                maxWidth: NAV_DESKTOP_BUTTON_MAX_WIDTH,
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}>{workerNavLabel}</AutoFitText>
              </Box>
            </Button>

            <Button
              startIcon={<Event />}
              onClick={() => navigate('/bookings')}
              sx={{ 
                color: '#DDD0C8',
                backgroundColor: isActive('/bookings') ? 'rgba(221, 208, 200, 0.1)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                minWidth: 0,
                maxWidth: NAV_DESKTOP_BUTTON_MAX_WIDTH,
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}><TranslatedText>Bookings</TranslatedText></AutoFitText>
              </Box>
            </Button>


            {/* Notifications - navigate to full notifications page (same as mobile) */}
            <IconButton
              onClick={() => navigate('/notifications')}
              sx={{ color: '#DDD0C8' }}
              aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* User Menu */}
            <IconButton
              size="large"
              onClick={handleMenu}
              sx={{ color: '#DDD0C8' }}
              ref={profileMenuButtonRef}
              aria-label="Account menu"
              aria-haspopup="true"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#DDD0C8', color: '#323232' }}>
                {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>

            {/* User Menu Dropdown */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => { navigate('/profile#account'); handleClose(); }}>
                <AccountBalanceWallet sx={{ mr: 1 }} />
                Account
              </MenuItem>
              <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                <AccountCircle sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={() => { navigate('/feedback'); handleClose(); }}>
                <RateReview sx={{ mr: 1 }} />
                Feedback
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>

          </Box>
          
          {/* Mobile: language + "more" menu (primary nav is in BottomNavBar) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            {selectedLang ? (
              <Button
                component="div"
                startIcon={<Language />}
                disableRipple
                disableElevation
                onClick={handleOpenLanguageMenu}
                className="notranslate"
                translate="no"
                sx={{
                  color: '#DDD0C8',
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'rgba(221, 208, 200, 0.1)' },
                  cursor: 'pointer',
                  mr: 0.5,
                  px: 1,
                  minWidth: 0,
                  maxWidth: 140,
                }}
              >
                <Box
                  component="span"
                  className="notranslate"
                  translate="no"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip' }}
                >
                  <AutoFitText className="notranslate" translate="no" minFontSize={10}>
                    {selectedLangLabel}
                  </AutoFitText>
                </Box>
              </Button>
            ) : (
              <IconButton
                onClick={handleOpenLanguageMenu}
                className="notranslate"
                translate="no"
                sx={{ color: '#DDD0C8', mr: 0.5 }}
                aria-label="select language"
              >
                <Language />
              </IconButton>
            )}
            {/* "More" hamburger — secondary items only; primary nav lives in BottomNavBar */}
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{ color: '#DDD0C8' }}
              aria-label="more options"
              ref={mobileMenuButtonRef}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileAnchor}
              open={Boolean(mobileAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {/* Always offer "Install App" in the drawer unless it's already
                  installed. promptInstall() falls back to manual instructions
                  when the native prompt isn't available. */}
              {!isInstalled && (
                <MenuItem onClick={() => { promptInstall(); handleMobileMenuClose(); }}>
                  <GetApp sx={{ mr: 1 }} />
                  <Box component="span" sx={{ ...NAV_SINGLELINE_SX, maxWidth: 220 }}>
                    <AutoFitText minFontSize={11}>Install App</AutoFitText>
                  </Box>
                </MenuItem>
              )}
              <MenuItem onClick={() => { navigate('/profile#account'); handleMobileMenuClose(); }}>
                <AccountBalanceWallet sx={{ mr: 1 }} />
                <Box component="span" sx={{ ...NAV_SINGLELINE_SX, maxWidth: 220 }}>
                  <AutoFitText minFontSize={11}>Account / Subscription</AutoFitText>
                </Box>
              </MenuItem>
              <MenuItem onClick={() => { navigate('/worker/profile'); handleMobileMenuClose(); }}>
                <Work sx={{ mr: 1 }} />
                <Box component="span" sx={{ ...NAV_SINGLELINE_SX, maxWidth: 220 }}>
                  <AutoFitText minFontSize={11}>{workerNavLabel}</AutoFitText>
                </Box>
              </MenuItem>
              <MenuItem onClick={() => { navigate('/feedback'); handleMobileMenuClose(); }}>
                <RateReview sx={{ mr: 1 }} />
                <Box component="span" sx={{ ...NAV_SINGLELINE_SX, maxWidth: 220 }}>
                  <AutoFitText minFontSize={11}>Feedback</AutoFitText>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                <Box component="span" sx={{ ...NAV_SINGLELINE_SX, maxWidth: 220 }}>
                  <AutoFitText minFontSize={11}>Logout</AutoFitText>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ 
                color: '#DDD0C8',
                borderColor: '#DDD0C8',
                minWidth: 0,
                maxWidth: 160,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { 
                  backgroundColor: 'rgba(221, 208, 200, 0.1)',
                  borderColor: '#DDD0C8'
                }
              }}
            >
              <Box component="span" sx={NAV_SINGLELINE_SX}>
                <AutoFitText minFontSize={11}>Sign In</AutoFitText>
              </Box>
            </Button>
          </Box>
        )}
        </Box>


      </Toolbar>
    </AppBar>
  );
};

export default Navbar;